// client/src/components/public/PublicFooter.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PRIVACY_LINE } from '../../utils/engagement';
import usePublicOffice, { illinoisContact } from '../../hooks/usePublicOffice';
import { ContactList, contactLines, IL_CONTACT_FALLBACK } from './PublicContact';

// Florida: every fact from the portal's own data (audit 2026-10, H8). Illinois: the
// managing broker's name, licence, contact and address from Virtual Office Settings (O8).
const FloridaCompliance = ({ jurisdiction, office }) => {
    const mb = office && office.managing_broker;
    return (
        <>
            <div>
                <h3 className="font-bold text-lg mb-2">Compliance Information</h3>
                <p className="text-gray-400 text-sm">Brokerage services offered in: {(office && office.service_area) || (jurisdiction && jurisdiction.name) || 'Florida'}</p>
                {office && office.physical_address && <p className="text-gray-400 text-sm mt-1">Office: {office.physical_address}</p>}
            </div>
            <div>
                <h3 className="font-bold text-lg mb-2">Broker</h3>
                {mb ? (
                    <>
                        <p className="text-gray-400 text-sm">{mb.name}{mb.license_number ? `, License #${mb.license_number}` : ''}</p>
                        {contactLines(office.contact).length ? (
                            <ContactList contact={office.contact} lineClass="text-gray-400 text-sm mt-1" linkClass="hover:text-indigo-400" />
                        ) : (
                            <>
                                {mb.email && <p className="text-gray-400 text-sm mt-1">Email: <a href={`mailto:${mb.email}`} className="hover:text-indigo-400">{mb.email}</a></p>}
                                {mb.phone && <p className="text-gray-400 text-sm mt-1">Phone: {mb.phone}</p>}
                            </>
                        )}
                    </>
                ) : <p className="text-gray-400 text-sm"><Link to="/office-registry" className="hover:text-indigo-400">Office registry</Link></p>}
            </div>
        </>
    );
};

// The brokerage's social profiles, kept at Administration → Social Media (/api/public/social).
const SocialLinks = () => {
    const [profiles, setProfiles] = useState([]);
    useEffect(() => {
        let live = true;
        fetch('/api/public/social').then((r) => (r.ok ? r.json() : null))
            .then((j) => { if (live && j && Array.isArray(j.profiles)) setProfiles(j.profiles); })
            .catch(() => {});
        return () => { live = false; };
    }, []);
    if (!profiles.length) return null;
    return (
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm mb-4" aria-label="Follow us">
            {profiles.map((p) => (p.url ? (
                <a key={p.network} href={p.url} target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white underline-offset-2 hover:underline">{p.label}</a>
            ) : (
                <span key={p.network} className="text-gray-300">
                    {p.label}: {[p.wechat_id, p.phone].filter((v, i, a) => v && a.indexOf(v) === i).join(' · ')}
                </span>
            )))}
        </div>
    );
};

const ilContact = (office) => (office && contactLines(office.contact).length ? office.contact : IL_CONTACT_FALLBACK);

const PublicFooter = () => {
    const { isFL, jurisdiction, office } = usePublicOffice();
    const pc = illinoisContact(office);
    return (
    <footer className="bg-gray-800 text-white">
        <div className="container mx-auto px-6 py-10">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
                <div>
                    <h3 className="font-bold text-lg mb-2">Business Real Estate Corp.</h3>
                    <p className="text-gray-400">Commercial Real Estate Professionals</p>
                </div>
                {isFL ? <FloridaCompliance jurisdiction={jurisdiction} office={office} /> : (
                <>
                <div>
                    <h3 className="font-bold text-lg mb-2">Compliance Information</h3>
                    <p className="text-gray-400 text-sm">Brokerage services offered in: Chicago, Illinois</p>
                    <p className="text-gray-400 text-sm mt-1">Physical address for service of process: {pc.public_service_address}</p>
                </div>
                <div>
                    <h3 className="font-bold text-lg mb-2">Designated Managing Broker</h3>
                    <p className="text-gray-400 text-sm">{pc.public_broker_name}{pc.public_broker_license ? `, License #${pc.public_broker_license}` : ''}</p>
                    <ContactList contact={ilContact(office)} lineClass="text-gray-400 text-sm mt-1" linkClass="hover:text-indigo-400" />
                </div>
                </>
                )}
            </div>
            <div className="text-center text-gray-500 border-t border-gray-700 mt-8 pt-6">
                <SocialLinks />
                <p>&copy; {new Date().getFullYear()} Business Real Estate Corp. All Rights Reserved.</p>
                <p className="text-sm mt-1"><Link to="/testimonials" className="hover:text-indigo-400">Client testimonials</Link></p>
                <p className="text-xs mt-3 max-w-3xl mx-auto text-gray-400"><strong>Privacy:</strong> {PRIVACY_LINE}</p>
            </div>
        </div>
    </footer>
    );
};

export default PublicFooter;
